import { ProNavigation } from "@/components/ProNavigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function VegetableOilsLibrary() {
  const navigate = useNavigate();

  const vegetableOils = [
    {
      id: "amande",
      name: "🥜 Huile d'Amande Douce",
      properties: [
        { icon: "💧", text: "Adoucissante, nourrissante, apaisante, émolliente" },
        { icon: "👶", text: "Parfaite pour les bébés (croûtes de lait, peau sensible)" },
        { icon: "🤰", text: "Prévention des vergetures" },
        { icon: "🧬", text: "Riche en vitamines A et E" }
      ]
    },
    {
      id: "argan",
      name: "🌰 Huile d'Argan",
      properties: [
        { icon: "♻️", text: "Régénérante, hydratante, anti-âge" },
        { icon: "🌵", text: "Pour peaux matures, irritées, eczéma" },
        { icon: "💇‍♀️", text: "Soin capillaire (cheveux cassants)" },
        { icon: "🧬", text: "Riche en acides gras essentiels" }
      ]
    },
    {
      id: "avocat",
      name: "🥑 Huile d'Avocat",
      properties: [
        { icon: "💧", text: "Nourrissante, hydratante" },
        { icon: "💇‍♀️", text: "Cheveux secs, cassants" },
        { icon: "🍊", text: "Anti-cellulite, peau terne" },
        { icon: "🧬", text: "Riche en acides gras et vitamines" }
      ]
    },
    {
      id: "calophylle",
      name: "🌿 Huile de Calophylle",
      properties: [
        { icon: "🔥", text: "Anti-inflammatoire, circulatoire" },
        { icon: "💜", text: "Varices, jambes lourdes, couperose" },
        { icon: "✨", text: "Cicatrisante, régénérante" },
        { icon: "👩‍⚕️", text: "Problèmes de peau (acné, eczéma)" }
      ]
    },
    {
      id: "coco",
      name: "🥥 Huile de Coco",
      properties: [
        { icon: "💧", text: "Hydratante, nourrissante" },
        { icon: "🦠", text: "Antibactérienne, antifongique" },
        { icon: "💆‍♀️", text: "Idéale pour cheveux secs et abîmés" },
        { icon: "🦷", text: "Oil pulling (hygiène buccale)" }
      ]
    },
    {
      id: "nigelle",
      name: "🌿 Huile de Nigelle",
      properties: [
        { icon: "🛡️", text: "Antioxydante, anti-inflammatoire, antibactérienne" },
        { icon: "🌬️", text: "Soutien du système immunitaire" },
        { icon: "👩‍🦰", text: "Acné, eczéma, psoriasis, chute de cheveux" },
        { icon: "🥗", text: "Usage interne (salades) et externe" }
      ]
    },
    {
      id: "jojoba",
      name: "🌾 Huile de Jojoba",
      properties: [
        { icon: "⚖️", text: "Équilibrante, régulatrice du sébum" },
        { icon: "💦", text: "Nourrissante, adaptée à tous types de peau" },
        { icon: "💇‍♀️", text: "Cheveux gras ou secs" },
        { icon: "📌", text: "Ne laisse pas de film gras" }
      ]
    },
    {
      id: "macadamia",
      name: "🌰 Huile de Macadamia",
      properties: [
        { icon: "♻️", text: "Régénérante, assouplissante" },
        { icon: "🧬", text: "Riche en acide palmitoléique" },
        { icon: "✨", text: "Peaux matures, vergetures, cicatrices" },
        { icon: "💆", text: "Excellente pour les massages" }
      ]
    },
    {
      id: "neem",
      name: "🌿 Huile de Neem",
      properties: [
        { icon: "🦠", text: "Antibactérienne, antifongique, purifiante" },
        { icon: "👩‍⚕️", text: "Acné, mycoses, psoriasis, eczéma" },
        { icon: "🐛", text: "Antiparasitaire (pellicules)" },
        { icon: "⚠️", text: "Odeur forte, à diluer" }
      ]
    },
    {
      id: "noisette",
      name: "🌰 Huile de Noisette",
      properties: [
        { icon: "💨", text: "Pénétrante, astringente" },
        { icon: "⚖️", text: "Régulatrice du sébum" },
        { icon: "✨", text: "Adaptée aux peaux grasses" },
        { icon: "💆", text: "Excellent support de massage" }
      ]
    },
    {
      id: "onagre",
      name: "🌸 Huile d'Onagre",
      properties: [
        { icon: "⚖️", text: "Régulatrice hormonale" },
        { icon: "🔥", text: "Anti-inflammatoire" },
        { icon: "👩", text: "SPM, ménopause, troubles hormonaux" },
        { icon: "✨", text: "Eczéma, peau sèche" }
      ]
    },
    {
      id: "pepin-raisin",
      name: "🍇 Huile de Pépin de Raisin",
      properties: [
        { icon: "🛡️", text: "Antioxydante, régénérante" },
        { icon: "✨", text: "Raffermissante, anti-âge" },
        { icon: "💧", text: "Adaptée à tous types de peau" },
        { icon: "💨", text: "Pénètre rapidement, non grasse" }
      ]
    },
    {
      id: "ricin",
      name: "🧴 Huile de Ricin",
      properties: [
        { icon: "💇‍♀️", text: "Fortifiante des cheveux, cils, sourcils" },
        { icon: "💅", text: "Renforce les ongles" },
        { icon: "✨", text: "Cicatrisante, émolliente" },
        { icon: "📌", text: "Favorise la pousse des cheveux" }
      ]
    },
    {
      id: "rose-musquee",
      name: "🌹 Huile de Rose Musquée",
      properties: [
        { icon: "♻️", text: "Régénérante, cicatrisante" },
        { icon: "🛡️", text: "Antioxydante, anti-âge" },
        { icon: "🧬", text: "Riche en vitamines A et C" },
        { icon: "✨", text: "Vergetures, cicatrices, taches" }
      ]
    },
    {
      id: "sesame",
      name: "🫘 Huile de Sésame",
      properties: [
        { icon: "🛡️", text: "Antioxydante, nourrissante" },
        { icon: "⚖️", text: "Régulatrice du sébum" },
        { icon: "🧘", text: "Idéale pour massages ayurvédiques" },
        { icon: "☀️", text: "Protection solaire légère" }
      ]
    },
    {
      id: "tamanu",
      name: "🌿 Huile de Tamanu (Calophylle Inophyle)",
      properties: [
        { icon: "✨", text: "Cicatrisante, régénérante" },
        { icon: "🔥", text: "Anti-inflammatoire" },
        { icon: "👩‍⚕️", text: "Cicatrices, acné, eczéma, brûlures" },
        { icon: "💜", text: "Couperose, vergetures" }
      ]
    },
    {
      id: "tournesol",
      name: "🌻 Huile de Tournesol",
      properties: [
        { icon: "💧", text: "Émolliente, adoucissante" },
        { icon: "🧬", text: "Riche en vitamine E" },
        { icon: "💰", text: "Support économique pour HE" },
        { icon: "💆", text: "Idéale pour massages" }
      ]
    }
  ];

  const macerats = [
    {
      id: "arnica",
      name: "🌼 Macérât d'Arnica",
      properties: [
        { icon: "🔥", text: "Anti-inflammatoire, analgésique" },
        { icon: "💢", text: "Contusions, hématomes, bleus" },
        { icon: "💪", text: "Douleurs musculaires, courbatures" },
        { icon: "⚠️", text: "Ne pas appliquer sur plaie ouverte" }
      ]
    },
    {
      id: "calendula",
      name: "🌼 Macérât de Calendula",
      properties: [
        { icon: "💢", text: "Apaisant, cicatrisant, anti-inflammatoire" },
        { icon: "👶", text: "Peaux sensibles, bébés" },
        { icon: "🐝", text: "Rougeurs, piqûres, urticaire, irritations" },
        { icon: "✨", text: "Anti-allergique" }
      ]
    },
    {
      id: "carotte",
      name: "🥕 Macérât de Carotte",
      properties: [
        { icon: "🌞", text: "Prépare la peau au soleil, effet bonne mine" },
        { icon: "✨", text: "Régénérant, teint éclatant" },
        { icon: "🧬", text: "Riche en caroténoïdes" },
        { icon: "⚠️", text: "Ne remplace pas une crème solaire !", warning: true }
      ]
    },
    {
      id: "millepertuis",
      name: "🌺 Macérât de Millepertuis",
      properties: [
        { icon: "😌", text: "Antidépresseur naturel, apaisant" },
        { icon: "🔥", text: "Anti-inflammatoire" },
        { icon: "👩‍⚕️", text: "Brûlures, coups de soleil, hémorroïdes" },
        { icon: "⚠️", text: "Photosensibilisant : éviter soleil 24h après usage", warning: true }
      ]
    },
    {
      id: "camomille",
      name: "🌼 Macérât de Camomille Romaine",
      properties: [
        { icon: "😌", text: "Apaisant, anti-inflammatoire" },
        { icon: "👶", text: "Adapté aux peaux sensibles et bébés" },
        { icon: "🔴", text: "Irritations, rougeurs, démangeaisons" },
        { icon: "💆", text: "Calmant cutané" }
      ]
    },
    {
      id: "rose-musquee-macerat",
      name: "🌹 Macérât de Rose Musquée",
      properties: [
        { icon: "♻️", text: "Régénérant cutané, cicatrisant" },
        { icon: "✨", text: "Anti-âge" },
        { icon: "🧬", text: "Riche en acides gras essentiels" },
        { icon: "👩‍⚕️", text: "Cicatrices, vergetures, rides" }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-hero py-8 px-4">
      <ProNavigation />
      
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:justify-between">
          <Button variant="outline" size="sm" onClick={() => navigate('/treatments-library')}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Bibliothèque de Traitements</span>
            <span className="sm:hidden">Retour</span>
          </Button>
          <h1 className="text-lg sm:text-3xl font-bold text-primary">🫒 Huiles Végétales & Macérats</h1>
          <div className="hidden sm:block w-[140px]"></div>
        </div>

        {/* Différence clé */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              🧬 Différence clé
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-primary/10">
                    <th className="border border-border p-3 text-left">🔍 Terme</th>
                    <th className="border border-border p-3 text-left">🧴 Définition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-3 font-medium">Huile végétale 🌰</td>
                    <td className="border border-border p-3">Extraite par pression à froid d'une graine ou d'un fruit oléagineux</td>
                  </tr>
                  <tr className="bg-muted/30">
                    <td className="border border-border p-3 font-medium">Macérât huileux 🌼</td>
                    <td className="border border-border p-3">Résultat de macération de plantes dans une huile végétale (ex : calendula)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Utilisations générales */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              🛠️ Utilisations générales
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-primary">🔹</span>
                <span>Nourrir la peau et les cheveux 💆‍♀️</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">🔹</span>
                <span>Diluer des huiles essentielles 🧪</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">🔹</span>
                <span>Propriétés thérapeutiques variées (anti-inflammatoire, cicatrisante, hydratante, etc.)</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Huiles Végétales */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              🧴 Huiles Végétales ({vegetableOils.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {vegetableOils.map((oil) => (
                <AccordionItem key={oil.id} value={oil.id}>
                  <AccordionTrigger className="text-lg font-semibold">
                    {oil.name}
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-2 pl-4">
                      {oil.properties.map((prop, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span>{prop.icon}</span>
                          <span>{prop.text}</span>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        {/* Macérats Huileux */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              🌸 Macérats Huileux ({macerats.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {macerats.map((macerat) => (
                <AccordionItem key={macerat.id} value={macerat.id}>
                  <AccordionTrigger className="text-lg font-semibold">
                    {macerat.name}
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-2 pl-4">
                      {macerat.properties.map((prop, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span>{prop.icon}</span>
                          <span className={prop.warning ? "text-orange-600 font-medium" : ""}>
                            {prop.text}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        {/* Huiles + Huiles Essentielles */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              🧪 Huiles + Huiles Essentielles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">Les huiles végétales servent de support pour diluer les HE :</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span>🌸</span>
                <span><strong>Exemple :</strong> huile de jojoba + HE de lavande = soin apaisant</span>
              </li>
              <li className="flex items-start gap-2">
                <span>🍊</span>
                <span><strong>Exemple :</strong> huile d'avocat + HE de pamplemousse = soin anticellulite</span>
              </li>
              <li className="flex items-start gap-2">
                <span>💪</span>
                <span><strong>Exemple :</strong> macérât d'arnica + HE de gaulthérie = soin sportif</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Conclusion */}
        <Card className="mb-6 border-primary/30 bg-primary/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              ✅ Conclusion
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 font-medium">✔️ Les huiles végétales & macérats sont :</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span>🌱</span>
                <span>100% naturels</span>
              </li>
              <li className="flex items-start gap-2">
                <span>💧</span>
                <span>Bénéfiques pour la peau, les cheveux, et parfois à consommer</span>
              </li>
              <li className="flex items-start gap-2">
                <span>🤲</span>
                <span>Polyvalents : usage cosmétique, thérapeutique & alimentaire</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}